import React, { useEffect, useState, useRef } from "react";
import InputText from "./InputText";

const mainClass = "help-input-select-searchable";
const dropdownClass = `${mainClass}__container__dropdown`;

const InputSelectSearchable = (props) => {
	const { name, value, handleChange, options } = props;
	const [showOptions, setShowOptions] = useState(false);
	const [searchedValue, setSearchedValue] = useState("");
	const [selectedValue, setSelectedValue] = useState(null);
	const [triggerInputValidation, setTriggerInputValidation] = useState(0);
	const [currentOptionIndex, setCurrentOptionIndex] = useState(-1);
	const containerRef = useRef(null);
	const listRef = useRef(null);

	const filteredOptions = options?.filter(
		(option) =>
			!!option.value &&
			option?.text?.toLowerCase().includes(searchedValue?.toLowerCase())
	);

	const validateInput = () => {
		setTriggerInputValidation((trigger) => trigger + 1);
	};

	const handleInputTextChange = (event) => {
		setCurrentOptionIndex(-1);
		setSearchedValue(event?.target?.value || "");
		if (!event?.target?.value) {
			handleChange({
				target: {
					name,
					value: undefined,
				},
			});
			setSelectedValue(null);
		}
		if (!showOptions) setShowOptions(true);
	};

	const handleClick = () => {
		setShowOptions(!showOptions);
	};

	const selectOption = (selectedOption, optionIndex, hideOptions = true) => {
		if (!selectedOption) return;
		setSelectedValue(selectedOption);
		handleChange({
			target: {
				name,
				value: selectedOption.value,
			},
		});
		setCurrentOptionIndex(optionIndex);
		if (hideOptions) {
			setSearchedValue("");
			setShowOptions(false);
		} else {
			validateInput();
			listRef?.current?.children?.[optionIndex]?.focus();
		}
	};

	const handleClickOutside = (event) => {
		if (containerRef.current && !containerRef.current.contains(event.target)) {
			setShowOptions(false);
		}
	};

	const addClickOutsideListener = () => {
		document.addEventListener("mousedown", handleClickOutside);
	};

	const removeClickOutsideListener = () => {
		document.removeEventListener("mousedown", handleClickOutside);
	};

	const handleOnKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			setShowOptions(!showOptions);
		}
		if (event.key === "Escape") {
			event.preventDefault();
			setShowOptions(false);
		}
		if (event.key === "ArrowDown") {
			event.preventDefault();
			selectBasedOnIndex();
		}
		if (event.key === "ArrowUp") {
			event.preventDefault();
			selectBasedOnIndex(false);
		}
	};

	const selectBasedOnIndex = (forward = true) => {
		if (currentOptionIndex === -1) {
			if (forward) {
				selectOption(filteredOptions[0], 0, false);
			}
		} else {
			let newCurrentOptionIndex = currentOptionIndex;
			if (forward) {
				newCurrentOptionIndex++;
			} else {
				newCurrentOptionIndex--;
			}
			selectOption(
				filteredOptions[newCurrentOptionIndex],
				newCurrentOptionIndex,
				false
			);
		}
		return;
	};

	const checkOnBlur = (event) => {
		return !containerRef.current?.contains(event?.relatedTarget);
	};

	useEffect(() => {
		if (showOptions) {
			removeClickOutsideListener();
			addClickOutsideListener();
		} else if (!showOptions) {
			if (value) validateInput();
			removeClickOutsideListener();
			setSearchedValue("");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showOptions, value]);

	useEffect(() => {
		return () => {
			removeClickOutsideListener();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div
			className={mainClass}
			ref={containerRef}
			// eslint-disable-next-line jsx-a11y/role-has-required-aria-props
			role="combobox"
			aria-expanded={showOptions}
			onKeyDown={handleOnKeyDown}
		>
			<InputText
				{...props}
				value={searchedValue || selectedValue?.text}
				handleChange={handleInputTextChange}
				handleClick={handleClick}
				triggerInputValidation={triggerInputValidation}
				ariaControls="listbox"
				onBlur={checkOnBlur}
			>
				<div className={`${mainClass}__container`}>
					<ul
						className={dropdownClass}
						role="listbox"
						style={{
							display:
								showOptions && !!filteredOptions?.length ? "block" : "none",
						}}
						ref={listRef}
					>
						{filteredOptions.map((option, index) => {
							const isSelected = selectedValue?.text === option.text;
							return (
								<li
									aria-selected={isSelected}
									className={`${dropdownClass}__option${
										isSelected ? ` ${dropdownClass}__option--selected` : ""
									}`}
									key={`${option.value}-${index}`}
									role="option"
									tabIndex="0"
									onClick={() => {
										selectOption(option, index);
									}}
									onKeyDown={(event) => {
										if (event.key === "Enter") {
											event.stopPropagation();
											selectOption(option, index);
										}
									}}
								>
									{option.text}
								</li>
							);
						})}
					</ul>
				</div>
			</InputText>
		</div>
	);
};

export default InputSelectSearchable;
